import React from 'react';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment-timezone';
import type {
  RequiredAlertsListItem,
  RequiredAlertsListType,
} from '@nxsys/types/api/analytics';
import { formatMoney, toNumber } from '@nxsys/utils/number-helper';

type RecordLike = Record<string, unknown>;
type AlertColumn = ColumnsType<RequiredAlertsListItem>[number];
type AlignType = 'left' | 'center' | 'right';

export type RequiredAlertsListColumnsOptions = {
  onView?: (workerId: string) => void;
  /** When provided, the action becomes "Edit" (opens the quick-edit drawer)
   *  instead of "View" (navigates to the detail page) — NX-4682. */
  onEdit?: (workerId: string) => void;
};

const isDateLike = (v: unknown): boolean =>
  typeof v === 'string' &&
  /^\d{4}-\d{2}-\d{2}/.test(v) &&
  !Number.isNaN(Date.parse(v));

const formatCell = (value: unknown): React.ReactNode => {
  if (value == null || value === '') return '_';
  if (typeof value === 'number' && Number.isFinite(value))
    return value.toLocaleString();
  if (isDateLike(value))
    return moment(String(value)).tz('Europe/London').format('DD/MM/YYYY');
  return String(value);
};

/** Read a possibly-nested value, e.g. "companyPayment.companyName". */
const pick = (record: RecordLike, path: string): unknown =>
  path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') {
      return (acc as RecordLike)[key];
    }
    return undefined;
  }, record);

const textCol = (
  title: string,
  dataIndex: string,
  width = 150,
  align?: AlignType,
): AlertColumn => ({
  key: dataIndex,
  title,
  dataIndex,
  width,
  ...(align ? { align } : {}),
  render: (_: unknown, record: RequiredAlertsListItem) =>
    formatCell(pick(record as RecordLike, dataIndex)),
});

const moneyCol = (
  title: string,
  dataIndex: string,
  width = 150,
): AlertColumn => ({
  key: dataIndex,
  title,
  dataIndex,
  width,
  align: 'right',
  render: (_: unknown, record: RequiredAlertsListItem) => {
    const value = pick(record as RecordLike, dataIndex);
    if (value == null || value === '') return '_';
    return formatMoney(toNumber(value as string | number));
  },
});

const actionsCol = (
  onView?: (workerId: string) => void,
  onEdit?: (workerId: string) => void,
): AlertColumn => ({
  key: 'actions',
  title: 'Actions',
  fixed: 'left',
  align: 'center',
  width: 90,
  render: (_: unknown, record: RequiredAlertsListItem) => {
    const r = record as RecordLike;
    // Recurring rows carry the owning worker as `subcontractorId` (their own
    // `id` is the payment/deduction id); generic rows are the worker itself.
    const workerId = r.subcontractorId ?? r.id;
    // "Edit" opens the quick-edit drawer in place; "View" navigates away.
    const handler = onEdit ?? onView;
    if (!handler || workerId == null) return '_';
    return (
      <span
        className="cursor-pointer text-primary-500 hover:underline"
        onClick={() => handler(String(workerId))}
      >
        {onEdit ? 'Edit' : 'View'}
      </span>
    );
  },
});

/**
 * Per-popup column sets for the Required Alerts list. Each alert type surfaces
 * only the fields relevant to that popup (NX-4682), instead of the previous
 * one-size-fits-all 21-column layout. Employer / Primary Agency are read from
 * the nested `companyPayment` / `primaryAgency` objects returned by the API.
 */
const buildColumns = (
  type: RequiredAlertsListType,
  onView?: (workerId: string) => void,
  onEdit?: (workerId: string) => void,
): ColumnsType<RequiredAlertsListItem> => {
  const actions = actionsCol(onView, onEdit);
  const systemId = textCol('System ID', 'systemId', 130);
  const agencyRef = textCol('Agency Ref', 'agencyRef', 120);
  const fullName = textCol('Full Name', 'fullName', 180);
  const email = textCol('Email', 'email', 200);
  const mobile = textCol('Mobile', 'mobile', 140);
  // "Phone" (Missing Email popup): the worker's own mobile, falling back to
  // their emergency contact number, then blank — secondary contact fallback.
  const phone: AlertColumn = {
    key: 'phone',
    title: 'Phone',
    dataIndex: 'mobile',
    width: 140,
    render: (_: unknown, record: RequiredAlertsListItem) => {
      const r = record as RecordLike;
      const primary = pick(r, 'mobile');
      const value =
        primary != null && primary !== ''
          ? primary
          : pick(r, 'emergencyMobile');
      return formatCell(value);
    },
  };
  const employer = textCol('Employer', 'companyPayment.companyName', 180);
  const primaryAgency = textCol('Primary Agency', 'primaryAgency.name', 160);
  const payFrequency = textCol('Pay Frequency', 'payFrequency', 130, 'center');
  const registrationStatus = textCol(
    'Registration Status',
    'registrationStatus',
    150,
    'center',
  );
  const startDate = textCol('Start Date', 'startDate', 120, 'center');
  const endDate = textCol('End Date', 'endDate', 120, 'center');

  switch (type) {
    case 'onHold':
      return [
        actions,
        systemId,
        agencyRef,
        fullName,
        textCol('Hold Reason', 'holdReason', 220),
        primaryAgency,
        employer,
        registrationStatus,
        email,
        mobile,
      ];
    case 'withRecurringPayments':
      return [
        actions,
        systemId,
        agencyRef,
        fullName,
        email,
        mobile,
        employer,
        primaryAgency,
        payFrequency,
        textCol('Payment Description', 'description', 200),
        startDate,
        endDate,
        textCol('Recurring Period', 'minPaymentPeriod', 140, 'center'),
        moneyCol('Total Amount', 'totalAmount', 150),
      ];
    case 'withRecurringDeductions':
      return [
        actions,
        systemId,
        fullName,
        employer,
        primaryAgency,
        payFrequency,
        registrationStatus,
        textCol('Deduction Type', 'deductionType', 150),
        textCol('Description', 'description', 200),
        startDate,
        endDate,
        textCol('Recurring Period', 'minDeductionPeriod', 140, 'center'),
        textCol('Max Recurrence', 'maxRecurrence', 130, 'center'),
        moneyCol('Total Deduction Amount', 'totalDeductionAmount', 180),
        textCol('Tax Treatment', 'taxTreatment', 180),
        email,
        mobile,
      ];
    case 'missingEmail':
      // Email is the missing field, so it is intentionally omitted here.
      return [
        actions,
        systemId,
        agencyRef,
        fullName,
        mobile,
        phone,
        employer,
        primaryAgency,
      ];
    case 'missingRPNActionNeeded':
    case 'missingRPNAwaiting':
      return [
        actions,
        systemId,
        agencyRef,
        fullName,
        textCol('PPS Number', 'setting.ppsNumber', 150),
        {
          key: 'rpnRequestStatus',
          title: 'RPN Status',
          dataIndex: 'rpnRequestStatus',
          width: 200,
          render: (_: unknown, record: RequiredAlertsListItem) => {
            const r = record as RecordLike;
            const status = pick(r, 'rpnRequestStatus') as string | undefined;
            const reason = pick(r, 'rpnErrorReason') as string | undefined;
            if (!status || status === 'NONE') return 'No RPN';
            if (status === 'REQUESTED' || status === 'NOT_READY') {
              return 'Requested — awaiting Revenue';
            }
            if (status === 'RECEIVED') return 'Received';
            if (status === 'ERROR') {
              const reasonMap: Record<string, string> = {
                CERT: 'certificate',
                REVENUE_SETUP: 'revenue not set up',
                INVALID_PPS: 'invalid PPS',
                LEAVER: 'leaver',
                NOT_READY: 'awaiting Revenue',
                OTHER: 'error',
              };
              const humanReason = reason ? (reasonMap[reason] ?? reason) : 'error';
              return `Error: ${humanReason}`;
            }
            return formatCell(status);
          },
        },
        email,
        mobile,
        employer,
        primaryAgency,
      ];
    case 'missingNINumber':
    case 'missingPPSNumber':
    case 'missingAddress':
    default:
      return [
        actions,
        systemId,
        agencyRef,
        fullName,
        email,
        mobile,
        employer,
        primaryAgency,
      ];
  }
};

export const getRequiredAlertsListColumns = (
  type: RequiredAlertsListType,
  options: RequiredAlertsListColumnsOptions = {},
): ColumnsType<RequiredAlertsListItem> =>
  buildColumns(type, options.onView, options.onEdit);
